/**
 * Collection & Product Grid Interactive Script (Category-1)
 * Handles Filter Drawer, Sort Dropdown, Items Per Page, Quick View Modal.
 */

// This file may be included by more than one section on a page (collection
// grid + featured collection). Guard so the inits only run once.
if (!window.__spiCollectionInit) {
  window.__spiCollectionInit = true;
  document.addEventListener('DOMContentLoaded', () => {
    initSortDropdown();
    initFilterDrawer();
    initFilterSidebar();
    initCollectionPagination();
    initItemsPerPage();
    initQuickViewModal();
    initCardInteractions();
    initCollectionAjaxNav();
  });
}

/* ---------------------------------------------------------------------------
 * Shared helpers
 * ------------------------------------------------------------------------- */
function spiFormatMoney(cents) {
  const code =
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(
      cents / 100
    );
  } catch (e) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function spiOpenCartDrawer() {
  // Cart type "page" (Theme settings › Cart): go to the cart page instead.
  // The drawer script loads either way, so its presence alone isn't enough.
  const drawerMode =
    window.SpiCart && typeof window.SpiCart.isDrawerMode === 'function' && window.SpiCart.isDrawerMode();
  if (!drawerMode) {
    const drawerRoot = document.getElementById('spi_cart-drawer');
    window.location.href = (drawerRoot && drawerRoot.dataset.cartUrl) || window.routes.cart_url;
    return;
  }

  // Refresh the drawer's contents from the just-updated cart BEFORE sliding
  // it into view, so it opens already showing the newly added item instead
  // of opening on stale/empty content and visibly updating a beat later.
  if (window.SpiCart && typeof window.SpiCart.open === 'function') {
    if (typeof window.SpiCart.refresh === 'function') {
      window.SpiCart.refresh().then(() => window.SpiCart.open(true));
    } else {
      window.SpiCart.open();
    }
  } else {
    window.location.href = window.routes.cart_url;
  }
}

/* ---------------------------------------------------------------------------
 * Product card: swatch swap + quick add-to-cart
 * ------------------------------------------------------------------------- */
function initCardInteractions() {
  const cards = document.querySelectorAll('.spi_product-card');
  if (!cards.length) return;

  // Swatch → swap the card's primary image / price / active variant.
  cards.forEach((card) => {
    if (card.dataset.cardWired === 'true') return;
    card.dataset.cardWired = 'true';

    const swatches = card.querySelectorAll('.spi_swatch');
    if (!swatches.length) return;

    const imageEl = card.querySelector('.spi_product-image');
    const primaryImg = card.querySelector('.spi_product-image-link img:first-of-type');
    const priceEl = card.querySelector('.spi_product-price');
    const addBtn = card.querySelector('[data-card-add-to-cart]');

    // Once a swatch changes the image, keep showing it directly (ignore the
    // hover secondary-image swap) until the pointer leaves the card.
    if (imageEl) {
      card.addEventListener('mouseleave', () => {
        imageEl.classList.remove('spi_swatch-just-selected');
      });
    }

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        swatches.forEach((s) => s.classList.remove('active'));
        swatch.classList.add('active');

        const img = swatch.dataset.image;
        const price = swatch.dataset.price;
        const variantId = swatch.dataset.variantId;

        if (img && primaryImg) {
          primaryImg.srcset = '';
          primaryImg.src = img;
          if (imageEl) imageEl.classList.add('spi_swatch-just-selected');
        }
        if (price && priceEl) priceEl.textContent = price;
        if (variantId && addBtn) {
          // Each swatch carries the first in-stock variant of its colour, so
          // the cart icon adds exactly that; a fully sold-out colour disables it.
          addBtn.dataset.variantId = variantId;
          addBtn.disabled = swatch.dataset.available === 'false';
        }
      });
    });
  });

  // Cart icon → add the card's current variant straight to the cart. Other
  // options (e.g. size) take their first in-stock value; Quick View is there
  // for shoppers who want to choose.
  document.querySelectorAll('[data-card-add-to-cart]').forEach((btn) => {
    if (btn.dataset.cardWired === 'true') return;
    btn.dataset.cardWired = 'true';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (btn.disabled) return;

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const original = btn.innerHTML;
      btn.disabled = true;
      btn.classList.add('is-loading');

      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, data: data })))
        .then((result) => {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          btn.innerHTML = original;
          if (!result.ok) {
            // Refused (e.g. the cart already holds all available stock):
            // say why instead of opening a drawer with nothing added.
            window.alert(result.data.description || result.data.message || '');
            return;
          }
          spiOpenCartDrawer();
        })
        .catch(() => {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          btn.innerHTML = original;
        });
    });
  });
}

/* 1. Sort Dropdown */
function initSortDropdown() {
  const sortBtn = document.getElementById('spi_sortBtn');
  const sortDropdown = document.getElementById('spi_sortDropdown');

  if (!sortBtn || !sortDropdown) return;

  sortBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    sortDropdown.classList.toggle('active');
  });

  document.addEventListener('click', (e) => {
    if (!sortDropdown.contains(e.target) && !sortBtn.contains(e.target)) {
      sortDropdown.classList.remove('active');
    }
  });

  const sortOptions = sortDropdown.querySelectorAll('.spi_sort-option');
  sortOptions.forEach((option) => {
    option.addEventListener('click', () => {
      const value = option.dataset.value;
      if (!value) return;

      sortDropdown.classList.remove('active');

      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('sort_by', value);
      spiAjaxRefreshResults(window.location.pathname + '?' + urlParams.toString());
    });
  });
}

/* 2. Filter Drawer */
function initFilterDrawer() {
  const filterBtn = document.getElementById('spi_filterBtn');
  const filterDrawer = document.getElementById('spi_filterDrawer');
  const filterOverlay = document.getElementById('spi_filterOverlay');
  const filterClose = document.getElementById('spi_filterClose');
  const filterForm = document.getElementById('spi_filterForm');
  const filterApply = document.getElementById('spi_filterApply');

  if (!filterBtn || !filterDrawer || !filterOverlay) return;

  function openDrawer() {
    filterDrawer.classList.add('active');
    filterOverlay.classList.add('active');
    document.documentElement.style.overflow = 'hidden';
  }

  function closeDrawer() {
    filterDrawer.classList.remove('active');
    filterOverlay.classList.remove('active');
    document.documentElement.style.overflow = '';
  }

  filterBtn.addEventListener('click', openDrawer);
  if (filterClose) filterClose.addEventListener('click', closeDrawer);
  filterOverlay.addEventListener('click', closeDrawer);

  // Accordion triggers
  wireFilterAccordion(filterDrawer);

  /* --- dual price-range slider --- */
  initPriceRangeSlider(filterDrawer, {
    rangeMin: 'spi_fdRangeMin',
    rangeMax: 'spi_fdRangeMax',
    priceMin: 'spi_fdPriceMin',
    priceMax: 'spi_fdPriceMax',
    fill: 'spi_fdRangeFill',
    tipMin: 'spi_fdTipMin',
    tipMax: 'spi_fdTipMax'
  });

  if (filterApply && filterForm) {
    filterApply.addEventListener('click', () => {
      const formData = new FormData(filterForm);
      const params = new URLSearchParams(formData);
      closeDrawer();
      spiAjaxRefreshResults(window.location.pathname + '?' + params.toString());
    });
  }
}

/* 2b. Filter Sidebar (inline sticky layout)
 * Shares the accordion / price-range behaviour of the drawer, but the panel
 * lives beside the grid on desktop and slides in from the left on mobile.
 */
function initFilterSidebar() {
  const sidebar = document.getElementById('spi_filterSidebar');
  if (!sidebar) return;

  const filterBtn = document.getElementById('spi_filterBtn');
  const overlay = document.getElementById('spi_sidebarOverlay');
  const closeBtn = document.getElementById('spi_sidebarClose');
  const form = document.getElementById('spi_sidebarForm');
  const applyBtn = document.getElementById('spi_sidebarApplyBtn');

  /* --- mobile open / close --- */
  function openSidebar() {
    sidebar.classList.add('active');
    if (overlay) overlay.classList.add('active');
    document.documentElement.style.overflow = 'hidden';
  }
  function closeSidebar() {
    sidebar.classList.remove('active');
    if (overlay) overlay.classList.remove('active');
    document.documentElement.style.overflow = '';
  }
  if (filterBtn) filterBtn.addEventListener('click', openSidebar);
  if (closeBtn) closeBtn.addEventListener('click', closeSidebar);
  if (overlay) overlay.addEventListener('click', closeSidebar);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sidebar.classList.contains('active')) closeSidebar();
  });

  /* --- accordion groups --- */
  wireFilterAccordion(sidebar);

  /* --- dual price-range slider --- */
  initPriceRangeSlider(sidebar, {
    rangeMin: 'spi_sbRangeMin',
    rangeMax: 'spi_sbRangeMax',
    priceMin: 'spi_sbPriceMin',
    priceMax: 'spi_sbPriceMax',
    fill: 'spi_sbRangeFill',
    tipMin: 'spi_sbTipMin',
    tipMax: 'spi_sbTipMax'
  });

  /* --- apply: push the form's fields into the URL --- */
  if (applyBtn && form) {
    applyBtn.addEventListener('click', () => {
      const params = new URLSearchParams(new FormData(form));
      closeSidebar();
      spiAjaxRefreshResults(window.location.pathname + '?' + params.toString());
    });
  }
}

/* Shared by the filter drawer and the filter sidebar — both render the same
   dual-handle price slider markup (spi_filter-price-range), just with
   different id prefixes so the two can't collide if a page ever renders
   both (e.g. drawer stays in the DOM as a mobile fallback). */
function initPriceRangeSlider(scope, ids) {
  const wrap = scope.querySelector('.spi_filter-price-range');
  if (!wrap) return;

  const rangeMin = wrap.querySelector('#' + ids.rangeMin);
  const rangeMax = wrap.querySelector('#' + ids.rangeMax);
  const priceMin = wrap.querySelector('#' + ids.priceMin);
  const priceMax = wrap.querySelector('#' + ids.priceMax);
  const fill = wrap.querySelector('#' + ids.fill);
  const tipMin = wrap.querySelector('#' + ids.tipMin);
  const tipMax = wrap.querySelector('#' + ids.tipMax);
  if (!rangeMin || !rangeMax || !priceMin || !priceMax || !fill) return;

  const max = parseInt(rangeMin.max, 10) || 0;
  const minGap = Math.max(1, Math.round(max * 0.02));
  const symbolEl = wrap.querySelector('.spi_filter-price-field span');
  const symbol = (symbolEl && symbolEl.textContent.trim()) || '';

  function paint() {
    const lo = parseInt(rangeMin.value, 10);
    const hi = parseInt(rangeMax.value, 10);
    const loPct = max ? (lo / max) * 100 : 0;
    const hiPct = max ? (hi / max) * 100 : 100;

    fill.style.left = loPct + '%';
    fill.style.width = hiPct - loPct + '%';

    if (tipMin) {
      tipMin.style.left = loPct + '%';
      tipMin.style.transform = `translateX(-${loPct}%)`;
      tipMin.textContent = symbol + lo.toLocaleString();
    }
    if (tipMax) {
      tipMax.style.left = hiPct + '%';
      tipMax.style.transform = `translateX(-${hiPct}%)`;
      tipMax.textContent = symbol + hi.toLocaleString();
    }

    priceMin.value = lo;
    priceMax.value = hi;
  }

  rangeMin.addEventListener('input', () => {
    if (parseInt(rangeMin.value, 10) > parseInt(rangeMax.value, 10) - minGap) {
      rangeMin.value = parseInt(rangeMax.value, 10) - minGap;
    }
    paint();
  });
  rangeMax.addEventListener('input', () => {
    if (parseInt(rangeMax.value, 10) < parseInt(rangeMin.value, 10) + minGap) {
      rangeMax.value = parseInt(rangeMin.value, 10) + minGap;
    }
    paint();
  });

  function syncFromMin() {
    let v = parseInt(priceMin.value, 10) || 0;
    v = Math.min(Math.max(0, v), parseInt(rangeMax.value, 10) - minGap);
    rangeMin.value = v;
    paint();
  }
  function syncFromMax() {
    let v = parseInt(priceMax.value, 10) || max;
    v = Math.max(Math.min(max, v), parseInt(rangeMin.value, 10) + minGap);
    rangeMax.value = v;
    paint();
  }
  priceMin.addEventListener('change', syncFromMin);
  priceMax.addEventListener('change', syncFromMax);

  paint();
}

/* Shared by the filter drawer and sidebar (both at init, and again after an
   AJAX refresh replaces their markup): wires each group's expand/collapse
   trigger. */
function wireFilterAccordion(scope) {
  scope.querySelectorAll('.spi_filter-group-trigger').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const group = trigger.closest('.spi_filter-group');
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!isExpanded));
      group.classList.toggle('active');
    });
  });
}

/* 2d. AJAX collection/search refresh — sort, filter apply/clear, items-per-
 * page, and numbered-pagination links all land here instead of a full page
 * reload. Fetches the target URL, swaps the product grid/pagination, filter
 * panel(s) and sort/count UI in place, and updates the address bar via
 * history so back/forward still work.
 */
function spiAjaxRefreshResults(url, opts) {
  const listSection = document.getElementById('spi_productListSection');
  if (!listSection) {
    window.location.href = url;
    return;
  }

  const pushState = !opts || opts.pushState !== false;
  listSection.classList.add('spi_loading');

  fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    .then((res) => {
      if (!res.ok) throw new Error(String(res.status));
      return res.text();
    })
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      spiSwapCollectionResults(doc);
      if (pushState) history.pushState({ spiCollection: true }, '', url);

      const freshSection = document.getElementById('spi_productListSection');
      if (freshSection) {
        freshSection.classList.remove('spi_loading');
        freshSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    })
    .catch(() => {
      window.location.href = url;
    });
}

function spiSwapCollectionResults(doc) {
  // Product grid + pagination nav + infinite-scroll trigger.
  const oldSection = document.getElementById('spi_productListSection');
  const newSection = doc.getElementById('spi_productListSection');
  if (oldSection && newSection) {
    oldSection.replaceWith(newSection);
    initCollectionPagination();
    document.dispatchEvent(new CustomEvent('spi:products:appended'));
  }

  // "Filters" button badge count.
  const oldCount = document.getElementById('spi_filterActiveCount');
  const newCount = doc.getElementById('spi_filterActiveCount');
  if (oldCount && newCount) oldCount.innerHTML = newCount.innerHTML;

  // Filter drawer / sidebar contents (checkbox states, counts, price range).
  spiSyncFilterPanel('spi_filterForm', 'spi_filterDrawer', doc, {
    rangeMin: 'spi_fdRangeMin',
    rangeMax: 'spi_fdRangeMax',
    priceMin: 'spi_fdPriceMin',
    priceMax: 'spi_fdPriceMax',
    fill: 'spi_fdRangeFill',
    tipMin: 'spi_fdTipMin',
    tipMax: 'spi_fdTipMax'
  });
  spiSyncFilterPanel('spi_sidebarForm', 'spi_filterSidebar', doc, {
    rangeMin: 'spi_sbRangeMin',
    rangeMax: 'spi_sbRangeMax',
    priceMin: 'spi_sbPriceMin',
    priceMax: 'spi_sbPriceMax',
    fill: 'spi_sbRangeFill',
    tipMin: 'spi_sbTipMin',
    tipMax: 'spi_sbTipMax'
  });

  // Sort trigger label + active option.
  const oldSortText = document.querySelector('.spi_filter-sort-text');
  const newSortText = doc.querySelector('.spi_filter-sort-text');
  if (oldSortText && newSortText) oldSortText.textContent = newSortText.textContent;
  document.querySelectorAll('.spi_sort-option').forEach((opt) => {
    const match = doc.querySelector(
      '.spi_sort-option[data-value="' + opt.dataset.value + '"]'
    );
    const isActive = !!(match && match.classList.contains('active'));
    opt.classList.toggle('active', isActive);
    opt.setAttribute('aria-pressed', String(isActive));
  });

  // Items-per-page select.
  const oldSelect = document.getElementById('spi_itemsPerPageSelect');
  const newSelect = doc.getElementById('spi_itemsPerPageSelect');
  if (oldSelect && newSelect) oldSelect.value = newSelect.value;
}

/* Replaces a filter <form>'s contents (fresh counts/checked/active state
   from the server) while keeping whichever groups the shopper had open, then
   rewires the accordion triggers and price slider the innerHTML swap just
   tore out. */
function spiSyncFilterPanel(formId, scopeId, doc, priceIds) {
  const oldForm = document.getElementById(formId);
  const newForm = doc.getElementById(formId);
  if (!oldForm || !newForm) return;

  const expanded = [];
  oldForm.querySelectorAll('.spi_filter-group').forEach((group, i) => {
    if (group.classList.contains('active')) expanded.push(i);
  });

  oldForm.innerHTML = newForm.innerHTML;

  oldForm.querySelectorAll('.spi_filter-group').forEach((group, i) => {
    if (expanded.indexOf(i) === -1) return;
    group.classList.add('active');
    const trigger = group.querySelector('.spi_filter-group-trigger');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  });

  wireFilterAccordion(oldForm);

  const scope = document.getElementById(scopeId);
  if (scope) initPriceRangeSlider(scope, priceIds);
}

/* Cross-cutting AJAX wiring: numbered-pagination links (re-delegated since
   the grid they live in gets replaced on every refresh), the drawer/sidebar
   "Clear all" links, and browser back/forward. */
function initCollectionAjaxNav() {
  const listSection = document.getElementById('spi_productListSection');
  if (!listSection) return;

  document.addEventListener('click', (e) => {
    const pageLink = e.target.closest('.spi_pagination-btn[href]');
    if (pageLink) {
      e.preventDefault();
      spiAjaxRefreshResults(pageLink.href);
      return;
    }

    const clearLink = e.target.closest(
      '#spi_filterClear, #spi_sidebarClearAll, .spi_empty-clear-btn'
    );
    if (clearLink && clearLink.getAttribute('href')) {
      e.preventDefault();
      spiAjaxRefreshResults(clearLink.href);
    }
  });

  window.addEventListener('popstate', () => {
    spiAjaxRefreshResults(window.location.href, { pushState: false });
  });
}

/* 2c. Collection pagination — "Load more" button / Infinite scroll
 * Numbered pages stay in the DOM as a no-JS fallback; here we hide them and
 * fetch the next page's grid over the wire, appending cards in place.
 */
function initCollectionPagination() {
  const section = document.getElementById('spi_productListSection');
  if (!section) return;

  const mode = section.dataset.paginationType;
  if (mode !== 'load_more' && mode !== 'infinite') return;

  const grid = section.querySelector('#spi_productGrid');
  if (!grid) return;

  const numberedNav = section.querySelector('[data-pagination-nav]');
  if (numberedNav) numberedNav.hidden = true;

  const trigger = section.querySelector('[data-infinite-trigger]');
  if (!trigger) return; // already on the last page

  const loadMoreBtn = trigger.querySelector('[data-load-more]');
  const spinner = trigger.querySelector('[data-infinite-spinner]');
  const statusEl = trigger.querySelector('[data-infinite-status]');

  let nextUrl = trigger.dataset.nextUrl || '';
  let loading = false;
  let observer = null;

  const usingObserver = mode === 'infinite' && 'IntersectionObserver' in window;
  if (usingObserver && loadMoreBtn) loadMoreBtn.hidden = true;

  function setBusy(state) {
    loading = state;
    if (spinner) spinner.hidden = !state;
    if (loadMoreBtn) loadMoreBtn.disabled = state;
  }

  function done() {
    if (observer) observer.disconnect();
    trigger.remove();
  }

  function loadNext() {
    if (loading || !nextUrl) return;
    setBusy(true);
    if (statusEl) statusEl.textContent = '';

    fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.getElementById('spi_productGrid');

        if (newGrid && newGrid.children.length) {
          const frag = document.createDocumentFragment();
          Array.from(newGrid.children).forEach((child) => frag.appendChild(child));
          grid.appendChild(frag);
          document.dispatchEvent(new CustomEvent('spi:products:appended'));
        }

        const newTrigger = doc.querySelector('[data-infinite-trigger]');
        nextUrl = newTrigger ? newTrigger.dataset.nextUrl || '' : '';

        setBusy(false);
        if (!nextUrl) done();
      })
      .catch(() => {
        setBusy(false);
        if (loadMoreBtn) loadMoreBtn.hidden = false;
        if (statusEl) statusEl.textContent = window.themeStrings.loading_error;
      });
  }

  if (loadMoreBtn) loadMoreBtn.addEventListener('click', loadNext);

  if (usingObserver) {
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadNext();
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(trigger);
  }
}

/* 3. Items Per Page */
function initItemsPerPage() {
  const select = document.getElementById('spi_itemsPerPageSelect');
  if (!select) return;

  const currentParams = new URLSearchParams(window.location.search);
  const currentPaginate = currentParams.get('paginate_by');
  if (currentPaginate) {
    select.value = currentPaginate;
  }

  select.addEventListener('change', () => {
    const val = select.value;
    fetch(window.routes.cart_update_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { paginate_by: val } })
    }).finally(() => {
      const params = new URLSearchParams(window.location.search);
      params.set('paginate_by', val);
      params.delete('page');
      spiAjaxRefreshResults(window.location.pathname + '?' + params.toString());
    });
  });
}

/* 4. Quick View Modal */
function initQuickViewModal() {
  const overlay = document.getElementById('spi_quickViewOverlay');
  const modal = document.getElementById('spi_quickViewModal');
  const closeBtn = document.getElementById('spi_quickViewClose');
  if (!overlay || !modal) return;

  const els = {
    title: document.getElementById('qv_title'),
    price: document.getElementById('qv_price'),
    priceInfo: document.getElementById('qv_price_info'),
    compare: document.getElementById('qv_compare_price'),
    discount: document.getElementById('qv_discount_badge'),
    unitPrice: document.getElementById('qv_unit_price'),
    desc: document.getElementById('qv_description'),
    sliderTrack: document.getElementById('qv_slider_track'),
    sliderPrev: document.getElementById('qv_slider_prev'),
    sliderNext: document.getElementById('qv_slider_next'),
    sliderDots: document.getElementById('qv_slider_dots'),
    variants: document.getElementById('qv_variants'),
    accordions: document.getElementById('qv_accordions'),
    viewFull: document.getElementById('qv_view_full'),
    qtyInput: document.getElementById('qv_qty_input'),
    qtyMinus: document.getElementById('qv_qty_minus'),
    qtyPlus: document.getElementById('qv_qty_plus'),
    addToCart: document.getElementById('qv_add_to_cart'),
    buyNow: document.getElementById('qv_buy_now')
  };

  const ADD_LABEL =
    (els.addToCart && els.addToCart.querySelector('span') && els.addToCart.querySelector('span').textContent) ||
    window.themeStrings.add_to_cart;
  const SOLD_OUT_LABEL = window.themeStrings.sold_out;
  const PRODUCT_DETAILS_LABEL = modal.dataset.productDetailsLabel || 'Product details';
  const SHIPPING_LABEL = modal.dataset.shippingLabel || 'Shipping and returns';

  // Theme-editor config (data-* attributes rendered by quick-view-modal.liquid).
  const cfg = {
    showDescription: modal.dataset.showDescription !== 'false',
    descriptionLength: parseInt(modal.dataset.descriptionLength, 10) || 180,
    showAccordions: modal.dataset.showAccordions !== 'false',
    sizeChartUrl: modal.dataset.sizeChartUrl || ''
  };
  const shippingEl = document.getElementById('qv_shipping_content');
  const SHIPPING_HTML = shippingEl ? shippingEl.innerHTML.trim() : '';

  let currentProduct = null;
  let selectedVariant = null;
  let slideIndex = 0;

  /* --- open / close --- */
  function openModal() {
    overlay.classList.add('spi_active');
    overlay.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
  }
  function closeModal() {
    pauseSlideMedia(-1);
    overlay.classList.remove('spi_active');
    overlay.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  }
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('spi_active')) closeModal();
  });

  /* --- triggers (idempotent; also re-bound on shopify:section:load) --- */
  function bindQuickViewTriggers() {
    document.querySelectorAll('[data-quick-view-btn]').forEach((btn) => {
      if (btn.dataset.qvWired === 'true') return;
      btn.dataset.qvWired = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = btn.dataset.productHandle;
        if (!handle) return;
        btn.classList.add('is-loading');
        fetch(`${window.routes.root_url.replace(/\/$/, '')}/products/${handle}.js`)
          .then((res) => res.json())
          .then((product) => {
            populateModal(product);
            openModal();
          })
          .catch((err) => console.error('Quick view error:', err))
          .finally(() => btn.classList.remove('is-loading'));
      });
    });
  }
  bindQuickViewTriggers();

  document.addEventListener('shopify:section:load', () => {
    bindQuickViewTriggers();
    initCardInteractions();
  });

  // Cards appended by "Load more" / infinite scroll need the same wiring.
  document.addEventListener('spi:products:appended', () => {
    bindQuickViewTriggers();
    initCardInteractions();
  });

  /* --- quantity --- */
  function getQty() {
    return Math.max(1, parseInt(els.qtyInput && els.qtyInput.value, 10) || 1);
  }
  if (els.qtyMinus) {
    els.qtyMinus.addEventListener('click', () => {
      if (els.qtyInput) els.qtyInput.value = Math.max(1, getQty() - 1);
    });
  }
  if (els.qtyPlus) {
    els.qtyPlus.addEventListener('click', () => {
      if (els.qtyInput) els.qtyInput.value = getQty() + 1;
    });
  }

  /* --- slider arrows (bound once) --- */
  if (els.sliderPrev) els.sliderPrev.addEventListener('click', () => goToSlide(slideIndex - 1));
  if (els.sliderNext) els.sliderNext.addEventListener('click', () => goToSlide(slideIndex + 1));

  /* --- variant helpers --- */
  function currentChosen() {
    return Array.from(els.variants.querySelectorAll('[data-option-index]')).map(
      (g) => g.dataset.selectedValue || ''
    );
  }

  function optionIsAvailable(optionIndex, value, chosen) {
    return currentProduct.variants.some((v) => {
      if (!v.available) return false;
      if (v.options[optionIndex] !== value) return false;
      return chosen.every((cv, i) => i === optionIndex || !cv || v.options[i] === cv);
    });
  }

  function refreshOptionAvailability() {
    const chosen = currentChosen();
    els.variants.querySelectorAll('[data-option-index]').forEach((group) => {
      const optionIndex = parseInt(group.dataset.optionIndex, 10);
      group.querySelectorAll('button[data-value]').forEach((btn) => {
        btn.classList.toggle(
          'is-unavailable',
          !optionIsAvailable(optionIndex, btn.dataset.value, chosen)
        );
      });
    });
  }

  function renderVariantGroups() {
    els.variants.innerHTML = '';
    const opts = currentProduct.options || [];
    const isDefaultOnly =
      opts.length === 1 &&
      opts[0].values.length === 1 &&
      /^(title|default title)$/i.test(opts[0].values[0]);
    if (isDefaultOnly) return;

    opts.forEach((opt, optionIndex) => {
      const selected = selectedVariant.options[optionIndex] || opt.values[0];
      const nameLower = (opt.name || '').toLowerCase();
      const looksLikeSwatch = /colou?r|metal|material|finish/.test(nameLower);
      const looksLikeSize = /\bsize\b/.test(nameLower);

      // Use image swatches only when at least one value has a variant image.
      const hasVariantImages =
        looksLikeSwatch &&
        opt.values.some((val) =>
          currentProduct.variants.some(
            (v) => v.options[optionIndex] === val && v.featured_image && v.featured_image.src
          )
        );

      const group = document.createElement('div');
      group.className = 'spi_modal-section';
      group.dataset.optionIndex = String(optionIndex);
      group.dataset.selectedValue = selected;

      const header = document.createElement('div');
      header.className = 'spi_modal-section-header';
      header.innerHTML =
        `<span class="spi_modal-section-label">${opt.name}: ` +
        `<span class="spi_modal-active-value">${selected}</span></span>`;
      if (looksLikeSize && cfg.sizeChartUrl) {
        header.insertAdjacentHTML(
          'beforeend',
          `<a class="spi_modal-size-chart" href="${cfg.sizeChartUrl}" target="_blank" rel="noopener">Size chart</a>`
        );
      }
      group.appendChild(header);

      const list = document.createElement('div');
      list.className = hasVariantImages ? 'spi_modal-swatches' : 'spi_modal-options';

      opt.values.forEach((val) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.title = val;
        btn.dataset.value = val;

        if (hasVariantImages) {
          btn.className = 'spi_modal-swatch';
          const imgVariant = currentProduct.variants.find(
            (v) => v.options[optionIndex] === val && v.featured_image && v.featured_image.src
          );
          if (imgVariant) {
            const im = document.createElement('img');
            im.src = imgVariant.featured_image.src;
            im.alt = val;
            im.loading = 'lazy';
            btn.appendChild(im);
          } else {
            btn.classList.add('spi_modal-swatch--text');
            btn.textContent = val;
          }
        } else {
          btn.className = 'spi_modal-option-btn';
          btn.textContent = val;
        }

        if (val === selected) btn.classList.add('active');

        btn.addEventListener('click', () => {
          group.dataset.selectedValue = val;
          const activeVal = header.querySelector('.spi_modal-active-value');
          if (activeVal) activeVal.textContent = val;
          list.querySelectorAll('button').forEach((b) => b.classList.remove('active'));
          btn.classList.add('active');
          resolveSelectedVariant();
        });

        list.appendChild(btn);
      });

      group.appendChild(list);
      els.variants.appendChild(group);
    });

    refreshOptionAvailability();
  }

  function resolveSelectedVariant() {
    const chosen = currentChosen();
    selectedVariant =
      currentProduct.variants.find((v) => chosen.every((cv, i) => v.options[i] === cv)) ||
      selectedVariant;

    updatePrice();
    if (selectedVariant.featured_image && selectedVariant.featured_image.src) {
      goToImageSrc(selectedVariant.featured_image.src);
    }
    refreshOptionAvailability();
    updateActionState();
  }

  /* --- price / actions / gallery --- */
  function updatePrice() {
    if (els.price) els.price.textContent = spiFormatMoney(selectedVariant.price);
    const onSale =
      selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price;
    if (els.priceInfo) els.priceInfo.hidden = !onSale;
    if (onSale) {
      if (els.compare) els.compare.textContent = spiFormatMoney(selectedVariant.compare_at_price);
      if (els.discount) {
        const pct = Math.round(
          ((selectedVariant.compare_at_price - selectedVariant.price) /
            selectedVariant.compare_at_price) *
            100
        );
        els.discount.textContent = `${pct}% off`;
      }
    }

    // Unit price ("$2.50/100ml"), only for variants that have one.
    const measurement = selectedVariant.unit_price_measurement;
    if (els.unitPrice) {
      const hasUnitPrice = Boolean(measurement && selectedVariant.unit_price != null);
      els.unitPrice.hidden = !hasUnitPrice;
      els.unitPrice.textContent = hasUnitPrice
        ? `${spiFormatMoney(selectedVariant.unit_price)}/${
            measurement.reference_value !== 1 ? measurement.reference_value : ''
          }${measurement.reference_unit}`
        : '';
    }
  }

  function updateActionState() {
    const available = !!(selectedVariant && selectedVariant.available);
    if (els.addToCart) {
      els.addToCart.disabled = !available;
      const span = els.addToCart.querySelector('span');
      if (span) span.textContent = available ? ADD_LABEL : SOLD_OUT_LABEL;
    }
    if (els.buyNow) els.buyNow.disabled = !available;
  }

  /* --- image slider --- */
  function slideImages() {
    return els.sliderTrack ? Array.from(els.sliderTrack.children) : [];
  }

  /* Stop any video / YouTube / Vimeo that isn't on screen (or when the
     modal closes) so audio never keeps playing out of view. */
  function pauseSlideMedia(exceptIndex) {
    slideImages().forEach((slide, i) => {
      if (i === exceptIndex) return;
      slide.querySelectorAll('video').forEach((v) => v.pause());
      slide.querySelectorAll('iframe').forEach((f) => {
        const msg =
          f.dataset.host === 'youtube'
            ? '{"event":"command","func":"pauseVideo","args":""}'
            : '{"method":"pause"}';
        if (f.contentWindow) f.contentWindow.postMessage(msg, '*');
      });
    });
  }

  function goToSlide(n) {
    const imgs = slideImages();
    if (!imgs.length || !els.sliderTrack) return;
    slideIndex = (n + imgs.length) % imgs.length;
    els.sliderTrack.style.transform = `translateX(-${slideIndex * 100}%)`;
    pauseSlideMedia(slideIndex);
    if (els.sliderDots) {
      els.sliderDots
        .querySelectorAll('.spi_modal-dot')
        .forEach((d, i) => d.classList.toggle('active', i === slideIndex));
    }
  }

  function normSrc(s) {
    return (s || '').replace(/^https?:/, '').split('?')[0];
  }

  function goToImageSrc(src) {
    if (!src) return;
    const target = normSrc(src);
    const idx = slideImages().findIndex((slide) => {
      const im = slide.tagName === 'IMG' ? slide : slide.querySelector('img');
      return im && normSrc(im.getAttribute('src')) === target;
    });
    if (idx > -1) goToSlide(idx);
  }

  /* One slide per product media item — images, Shopify videos, YouTube /
     Vimeo and 3D models (rich media), same order as the product page. */
  function buildMediaSlide(media, i) {
    const title = currentProduct.title;
    const poster = media.preview_image && media.preview_image.src;

    if (media.media_type === 'video') {
      const slide = document.createElement('div');
      slide.className = 'spi_modal-slide spi_modal-slide--video';
      const video = document.createElement('video');
      video.controls = true;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      if (poster) video.poster = poster;
      video.setAttribute('aria-label', media.alt || title);
      (media.sources || [])
        .filter((s) => s.format === 'mp4' || /mp4/.test(s.mime_type || ''))
        .forEach((s) => {
          const source = document.createElement('source');
          source.src = s.url;
          source.type = s.mime_type || 'video/mp4';
          video.appendChild(source);
        });
      slide.appendChild(video);
      return slide;
    }

    if (media.media_type === 'external_video' && media.external_id) {
      const slide = document.createElement('div');
      slide.className = 'spi_modal-slide spi_modal-slide--external';
      const iframe = document.createElement('iframe');
      const id = encodeURIComponent(media.external_id);
      iframe.dataset.host = media.host;
      iframe.src =
        media.host === 'youtube'
          ? `https://www.youtube-nocookie.com/embed/${id}?enablejsapi=1&playsinline=1&rel=0`
          : `https://player.vimeo.com/video/${id}`;
      iframe.title = media.alt || title;
      iframe.loading = 'lazy';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      slide.appendChild(iframe);
      return slide;
    }

    if (media.media_type === 'model') {
      const glb = (media.sources || []).find((s) => s.format === 'glb');
      if (glb) {
        const slide = document.createElement('div');
        slide.className = 'spi_modal-slide spi_modal-slide--model';
        const viewer = document.createElement('model-viewer');
        viewer.setAttribute('src', glb.url);
        viewer.setAttribute('alt', media.alt || title);
        viewer.setAttribute('camera-controls', '');
        viewer.setAttribute('reveal', 'interaction');
        if (poster) viewer.setAttribute('poster', poster);
        slide.appendChild(viewer);
        return slide;
      }
    }

    const src = poster || media.src;
    if (!src) return null;
    const img = document.createElement('img');
    img.src = src;
    img.alt = media.alt || `${title} ${i + 1}`;
    img.loading = i === 0 ? 'eager' : 'lazy';
    img.draggable = false;
    return img;
  }

  /* <model-viewer> needs Shopify's viewer component; load it on demand. */
  function loadModelViewer() {
    const viewers = els.sliderTrack.querySelectorAll('model-viewer');
    if (!viewers.length || !window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    window.Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (errors) => {
          if (errors) return;
          viewers.forEach((el) => new window.Shopify.ModelViewerUI(el));
        }
      }
    ]);
  }

  function renderSlider() {
    if (!els.sliderTrack) return;

    els.sliderTrack.innerHTML = '';
    const media = (currentProduct.media || []).slice(0, 8);
    if (media.length) {
      media.forEach((m, i) => {
        const slide = buildMediaSlide(m, i);
        if (slide) els.sliderTrack.appendChild(slide);
      });
    } else {
      const srcs = (currentProduct.images || []).slice(0, 8);
      if (!srcs.length && currentProduct.featured_image) srcs.push(currentProduct.featured_image);
      srcs.forEach((src, i) => {
        const img = document.createElement('img');
        img.src = src;
        img.alt = `${currentProduct.title} ${i + 1}`;
        img.loading = i === 0 ? 'eager' : 'lazy';
        img.draggable = false;
        els.sliderTrack.appendChild(img);
      });
    }
    if (!els.sliderTrack.children.length) {
      const placeholder = document.getElementById('qv_slider_placeholder');
      if (placeholder) els.sliderTrack.appendChild(placeholder.content.cloneNode(true));
    }
    loadModelViewer();
    const srcs = slideImages();

    if (els.sliderDots) {
      els.sliderDots.innerHTML = '';
      srcs.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'spi_modal-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', `Go to image ${i + 1}`);
        dot.addEventListener('click', () => goToSlide(i));
        els.sliderDots.appendChild(dot);
      });
      els.sliderDots.hidden = srcs.length < 2;
    }

    const multiple = srcs.length > 1;
    if (els.sliderPrev) els.sliderPrev.hidden = !multiple;
    if (els.sliderNext) els.sliderNext.hidden = !multiple;

    slideIndex = 0;
    els.sliderTrack.style.transition = 'none';
    els.sliderTrack.style.transform = 'translateX(0)';
    // restore the animated transition on the next frame
    requestAnimationFrame(() => {
      els.sliderTrack.style.transition = '';
    });
  }

  function renderAccordions() {
    if (!els.accordions) return;
    els.accordions.innerHTML = '';
    if (!cfg.showAccordions) {
      els.accordions.hidden = true;
      return;
    }
    els.accordions.hidden = false;

    const rows = [];
    if (currentProduct.description && currentProduct.description.trim()) {
      rows.push({ title: PRODUCT_DETAILS_LABEL, html: currentProduct.description, open: true });
    }
    if (SHIPPING_HTML) {
      rows.push({ title: SHIPPING_LABEL, html: SHIPPING_HTML, open: rows.length === 0 });
    }
    if (!rows.length) {
      els.accordions.hidden = true;
      return;
    }

    rows.forEach((row) => {
      const acc = document.createElement('div');
      acc.className = 'spi_modal-accordion' + (row.open ? ' active' : '');
      acc.innerHTML =
        '<button type="button" class="spi_modal-accordion-trigger">' +
        `<span>${row.title}</span><span class="spi_modal-accordion-icon" aria-hidden="true"></span>` +
        '</button>' +
        `<div class="spi_modal-accordion-content"><div class="spi_modal-accordion-inner">${row.html}</div></div>`;
      acc
        .querySelector('.spi_modal-accordion-trigger')
        .addEventListener('click', () => acc.classList.toggle('active'));
      els.accordions.appendChild(acc);
    });
  }

  /* --- populate --- */
  function populateModal(product) {
    currentProduct = product;
    selectedVariant = product.variants.find((v) => v.available) || product.variants[0];

    if (els.title) els.title.textContent = product.title;
    if (els.viewFull) els.viewFull.href = product.url;
    if (els.qtyInput) els.qtyInput.value = 1;
    showQvError('');

    if (els.desc) {
      const tmp = document.createElement('div');
      tmp.innerHTML = product.description || '';
      const text = (tmp.textContent || '').trim().replace(/\s+/g, ' ');
      const len = cfg.descriptionLength;
      if (cfg.showDescription && text) {
        els.desc.textContent = text.length > len ? `${text.slice(0, len).trim()}…` : text;
        els.desc.hidden = false;
      } else {
        els.desc.hidden = true;
      }
    }

    renderSlider();
    renderVariantGroups();
    if (selectedVariant.featured_image && selectedVariant.featured_image.src) {
      goToImageSrc(selectedVariant.featured_image.src);
    }
    updatePrice();
    updateActionState();
    renderAccordions();
  }

  /* --- cart actions --- */
  const cartErrorEl = document.getElementById('qv_cart_error');

  function showQvError(message) {
    if (!cartErrorEl) return;
    cartErrorEl.textContent = message || '';
    cartErrorEl.hidden = !message;
  }

  // Resolves only when Shopify accepted the add; otherwise rejects with
  // Shopify's own message (e.g. "You can't add more X to the cart").
  function addSelectedToCart() {
    showQvError('');
    return fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: selectedVariant.id, quantity: getQty() })
    }).then((res) =>
      res.json().then((data) => {
        if (!res.ok) throw new Error(data.description || data.message || '');
        return data;
      })
    );
  }

  if (els.addToCart) {
    els.addToCart.addEventListener('click', () => {
      if (els.addToCart.disabled) return;
      // Same "ADDING..." feedback as the product page button.
      const originalHtml = els.addToCart.innerHTML;
      els.addToCart.disabled = true;
      els.addToCart.innerHTML = `<span>${window.themeStrings.adding}</span>`;
      addSelectedToCart()
        .then(() => {
          closeModal();
          spiOpenCartDrawer();
        })
        .catch((err) => showQvError(err.message))
        .finally(() => {
          els.addToCart.innerHTML = originalHtml;
          els.addToCart.disabled = false;
        });
    });
  }

  if (els.buyNow) {
    els.buyNow.addEventListener('click', () => {
      if (els.buyNow.disabled) return;
      const originalHtml = els.buyNow.innerHTML;
      els.buyNow.disabled = true;
      els.buyNow.innerHTML = `<span>${window.themeStrings.please_wait}</span>`;
      addSelectedToCart()
        .then(() => {
          window.location.href = '/checkout';
        })
        .catch((err) => {
          showQvError(err.message);
          els.buyNow.innerHTML = originalHtml;
          els.buyNow.disabled = false;
        });
    });
  }
}
